import React from 'react';
import {
  DocumentTextIcon,
  ClockIcon,
  EyeIcon,
  DocumentArrowDownIcon,
  PencilIcon,
  ArrowPathIcon,
} from '../../icons';
import MatDashPanel from '../layout/MatDashPanel';

const VersionHistory = ({
  showVersions,
  versions,
  versionsLoading,
  selectedVersion,
  setSelectedVersion,
  downloadVersionAsPDF,
  downloadVersionAsDOCX,
  editVersion,
  restoreVersion,
  savingVersion,
  viewOriginalTranscript,
  originalContent,
  loading,
  error,
  isLocked = false,
  isCertified = false,
  aiDraftVersionNumber = null,
}) => {
  if (!showVersions) return null;

  return (
    <MatDashPanel
      title="Version History"
      subtitle={`${versions.length} version${versions.length !== 1 ? 's' : ''} available`}
      icon={ClockIcon}
      className="mt-4 sm:mt-6 mb-0"
    >
      {versionsLoading ? (
        <div className="md-empty-state py-6 sm:py-8">
          <div className="animate-spin rounded-full h-6 w-6 sm:h-8 sm:w-8 border-b-2 border-matdash-primary mx-auto" />
          <p className="mt-2 text-matdash-muted text-sm">Loading versions...</p>
        </div>
      ) : versions.length === 0 ? (
        <div className="md-empty-state py-6 sm:py-8">
          <DocumentTextIcon className="h-10 w-10 sm:h-12 sm:w-12 mx-auto mb-4 text-matdash-border" />
          <p className="text-sm">No versions available</p>
        </div>
      ) : (
        <div className="space-y-3">
          {versions.map((version) => {
            const isAiDraft =
              version.change_type === 'ai_draft' ||
              version.is_ai_draft === true ||
              (isCertified && aiDraftVersionNumber === version.version_number);

            return (
              <div
                key={version.id}
                className={`border rounded-md-lg p-3 sm:p-4 transition-colors ${
                  version.is_current ? 'md-version-current' : 'border-matdash-border hover:border-matdash-primary/30'
                }`}
              >
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
                  <div className="flex items-start space-x-3">
                    <div className="flex-shrink-0">
                      <DocumentTextIcon className="h-4 w-4 sm:h-5 sm:w-5 text-matdash-muted" />
                    </div>
                    <div className="flex-1">
                      <div className="flex flex-wrap items-center gap-2 mb-1">
                        <span className="text-sm font-medium text-matdash-dark">
                          Version {version.version_number}
                        </span>
                        {version.is_current && (
                          <span className="px-2 py-1 text-xs bg-matdash-success-subtle text-emerald-800 rounded-full border border-emerald-200">
                            Current
                          </span>
                        )}
                        {isAiDraft && (
                          <span className="px-2 py-1 text-xs bg-matdash-warning-subtle text-amber-900 rounded-full border border-matdash-warning/30">
                            AI Draft
                          </span>
                        )}
                        <span className="px-2 py-1 text-xs bg-matdash-body text-matdash-muted rounded-full border border-matdash-border">
                          {version.change_type}
                        </span>
                      </div>
                      <div className="flex flex-wrap items-center gap-3 text-xs text-matdash-muted">
                        <div className="flex items-center">
                          <ClockIcon className="h-3 w-3 mr-1" />
                          {new Date(version.created_at).toLocaleString()}
                        </div>
                        <div className="flex items-center">
                          <span className="font-medium text-matdash-dark">
                            {version.created_by_name || 'Unknown User'}
                          </span>
                        </div>
                        <div>{version.word_count} words</div>
                        <div>{version.character_count} characters</div>
                      </div>
                      {version.change_summary && (
                        <p className="text-xs text-matdash-muted mt-1">{version.change_summary}</p>
                      )}
                    </div>
                  </div>

                  <div className="flex flex-wrap items-center gap-2">
                    <button
                      onClick={() => setSelectedVersion(version)}
                      className="md-btn-outline-sm"
                    >
                      <EyeIcon className="h-3 w-3" />
                      View
                    </button>

                    <button
                      onClick={() => downloadVersionAsPDF(version)}
                      className="md-btn-outline-sm"
                    >
                      <DocumentArrowDownIcon className="h-3 w-3" />
                      PDF
                    </button>

                    <button
                      onClick={() => downloadVersionAsDOCX(version)}
                      className="md-btn-outline-sm text-emerald-700 hover:bg-emerald-50 border-emerald-200"
                    >
                      <DocumentArrowDownIcon className="h-3 w-3" />
                      DOCX
                    </button>

                    {!isLocked && !isAiDraft && (
                      <button
                        onClick={() => editVersion(version)}
                        className="md-btn-outline-sm text-indigo-700 hover:bg-indigo-50 border-indigo-200"
                      >
                        <PencilIcon className="h-3 w-3" />
                        Edit
                      </button>
                    )}

                    {!isLocked && !version.is_current && !isAiDraft && (
                      <button
                        onClick={() => {
                          if (window.confirm(`Restore to version ${version.version_number}? This will create a new version.`)) {
                            restoreVersion(version.version_number);
                          }
                        }}
                        disabled={savingVersion}
                        className="md-btn-outline-sm text-amber-700 hover:bg-amber-50 border-amber-200 disabled:opacity-50"
                      >
                        <ArrowPathIcon className="h-3 w-3" />
                        {savingVersion ? 'Restoring...' : 'Restore'}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className="mt-4 pt-4 border-t border-matdash-border">
        <button
          onClick={viewOriginalTranscript}
          disabled={loading || error || !originalContent}
          className="w-full md-btn md-btn-outline justify-center disabled:opacity-50 disabled:cursor-not-allowed text-sm text-amber-800 border-amber-200 hover:bg-amber-50"
        >
          <DocumentTextIcon className="h-4 w-4 sm:h-5 sm:w-5 mr-2" />
          {isCertified ? 'View AI Draft (read-only)' : 'View Original Transcript'}
        </button>
      </div>
    </MatDashPanel>
  );
};

export default VersionHistory;
